import React, { useState } from 'react';
import { FaTimes } from '@react-icons/all-files/fa/FaTimes';
import UsuariosFilter from '../../../commons/UsuariosFilter';
import type { ProveedorEmpresaConfigApi } from './proveedor-config-preview';

export type ProveedorContadorEntry = { usuario: Record<string, unknown> };

export function parseProveedorEmpresaContadores(
  config: ProveedorEmpresaConfigApi | null,
): ProveedorContadorEntry[] {
  const raw = config?.contadores;
  if (!Array.isArray(raw)) return [];
  const out: ProveedorContadorEntry[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const usuario = row.usuario;
    if (usuario != null && typeof usuario === 'object' && !Array.isArray(usuario)) {
      const u = usuario as Record<string, unknown>;
      if (u.id != null) {
        out.push({ usuario: u });
        continue;
      }
    }
    if (row.id != null && (row.nombre != null || row.usuario != null || row.email != null)) {
      out.push({ usuario: row });
    }
  }
  return out;
}

function usuarioLabel(usuario: Record<string, unknown>): string {
  const nombre = `${usuario.nombre ?? ''} ${usuario.apellido ?? ''}`.trim();
  if (nombre) return nombre;
  const login = usuario.usuario ?? usuario.email;
  if (login != null && String(login).trim() !== '') return String(login).trim();
  return `ID ${usuario.id}`;
}

export default function ProveedorContadoresSection({
  contadores,
  onChange,
  disabled = false,
}: {
  contadores: ProveedorContadorEntry[];
  onChange: (next: ProveedorContadorEntry[]) => void;
  disabled?: boolean;
}) {
  const [filterValue, setFilterValue] = useState<Record<string, unknown> | null>(null);

  const handleSelect = (usuario: Record<string, unknown> | null) => {
    if (disabled || usuario == null || usuario.id == null) return;
    setFilterValue(null);
    const id = Number(usuario.id);
    if (contadores.some(c => Number(c.usuario?.id) === id)) return;
    onChange([...contadores, { usuario }]);
  };

  const handleRemove = (usuarioId: number) => {
    if (disabled) return;
    onChange(contadores.filter(c => Number(c.usuario?.id) !== usuarioId));
  };

  return (
    <section
      style={{
        borderTop: '1px solid #f1f5f9',
        paddingTop: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
      aria-labelledby="proveedor-contadores-section-title"
    >
      <h3
        id="proveedor-contadores-section-title"
        className="m-0"
        style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}
      >
        Contador Asignado
      </h3>
      {contadores.length === 0 ? (
        <p className="m-0" style={{ fontSize: 12, color: '#94a3b8' }}>
          Ningún contador asignado.
        </p>
      ) : (
        <ul className="list-unstyled m-0 p-0 d-flex flex-column gap-2">
          {contadores.map(entry => {
            const id = Number(entry.usuario?.id);
            return (
              <li
                key={id}
                className="d-flex align-items-center justify-content-between gap-2 rounded"
                style={{
                  padding: '8px 10px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  fontSize: 13,
                }}
              >
                <span style={{ color: '#0f172a', fontWeight: 500 }}>{usuarioLabel(entry.usuario)}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-light border"
                  style={{ padding: '2px 8px', lineHeight: 1 }}
                  aria-label={`Quitar ${usuarioLabel(entry.usuario)}`}
                  disabled={disabled}
                  onClick={() => handleRemove(id)}
                >
                  <FaTimes size={12} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <UsuariosFilter
        value={filterValue}
        onChange={handleSelect}
        placeholder="Buscar usuario…"
        disabled={disabled}
      />
    </section>
  );
}
